import { Product } from "types/Product";

export type DeliveryStatus =
  | "draft"
  | "planned"
  | "shipped"
  | "delivered"
  | "cancelled";

export type DeliverySource = "manual" | "terminalas";

export type DeliveryWarningCode =
  | "OVER_RESERVED"
  | "OVER_ORDERED"
  | "PRODUCT_UNKNOWN"
  | "PRODUCT_NOT_IN_CONTRACT"
  | "PRODUCT_NOT_IN_RESERVATION"
  | "ORDER_NOT_SELLABLE"
  | string;

export interface DeliveryWarning {
  code: DeliveryWarningCode;
  sku?: string;
  reserved?: number;
  ordered?: number;
  shipped?: number;
}

export interface DeliveryUnmatchedLine {
  code: string;
  title?: string;
  quantity: number;
}

export interface DeliveryExternalMeta {
  warnings?: DeliveryWarning[];
  unmatchedLines?: DeliveryUnmatchedLine[];
  buyerName?: string;
}

export interface DeliveryItem {
  id: string;
  delivery: string;
  product: string | Product;
  reservationItem?: string | null;
  reservationNumber?: number | null;
  quantity: number;
  orderLineType: "orderItem" | "orderProductGroup" | null;
  orderLineId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DeliveryItemInput {
  id?: string;
  product?: string;
  reservationItem: string;
  quantity: number;
  orderLineType?: "orderItem" | "orderProductGroup" | null;
  orderLineId?: string | null;
  notes?: string | null;
}

export interface DeliveryUser {
  id: string;
  name: string;
  email?: string;
}

export interface DeliveryOrderRef {
  id: string;
  number: number;
  _number: string;
}

export interface DeliveryCustomerRef {
  id: string;
  name: string;
}

export interface Delivery {
  id: string;
  order: string | DeliveryOrderRef;
  createdBy: DeliveryUser | null;
  status: DeliveryStatus;
  number: number;
  deliveryDate: string | null;
  notes: string | null;
  carrier: string | null;
  items?: DeliveryItem[];
  customer?: DeliveryCustomerRef | null;
  orderDetails?: {
    id: string;
    number: number;
    _number: string;
    date: string;
    customer: string;
  };
  createdAt: string;
  updatedAt: string;
  // terminalas sync (optional — absent on manual/older rows)
  source?: DeliverySource;
  externalId?: string | null;
  reservation?: string | null;
  vehiclePlate?: string | null;
  driverName?: string | null;
  carrierName?: string | null;
  loadedAt?: string | null;
  gateExitAt?: string | null;
  netWeightKg?: number | null;
  externalOrderCode?: string | null;
  externalUrl?: string | null;
  // backend may send this as a JSON string or an already-parsed object —
  // use parseDeliveryExternalMeta() rather than reading it directly.
  externalMeta?: DeliveryExternalMeta | string | null;
}

export interface CreateDeliveryInput {
  deliveryDate?: string | null;
  notes?: string | null;
  carrier?: string | null;
  items?: DeliveryItemInput[];
}

export interface UpdateDeliveryInput {
  status?: DeliveryStatus;
  deliveryDate?: string | null;
  notes?: string | null;
  carrier?: string | null;
  items?: DeliveryItemInput[];
}

export interface FetchDeliveriesResponse {
  deliveries: Delivery[];
}

export interface DeliveryResponse {
  delivery: Delivery;
}

export interface DeliveryItemResponse {
  item: DeliveryItem;
}

export interface DeleteResponse {
  success: boolean;
  message: string;
}

/**
 * `Delivery.externalMeta` comes from the backend's LONGTEXT JSON column and
 * can arrive either already parsed (object) or as a raw string, depending on
 * the endpoint — parse it defensively rather than assuming either shape.
 */
export function parseDeliveryExternalMeta(
  raw: Delivery["externalMeta"]
): DeliveryExternalMeta {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}
